import { useEffect } from 'react';
import useConstant from 'use-constant';
import produce from 'immer';
import { useGate } from 'effector-react';
import { format, fromUnixTime } from 'date-fns';
import { ImageType, LibType, type SceneCompareItem, SceneType } from '@types';
import { Collapse } from '@components/Collapse';
import Title from '@components/Title';
import PanelBase from '@modules/panel/PanelBase';
import Button from '@components/Button';
import ImageContainer from '@components/ImageContainer';
import Tip from '@components/Tip';
import { Validator } from '@components/Validator';
import Image from '@components/Image';
import { selectLibScenes } from '@modules/mediaLib';
import useInputHandlers from '@hooks/useInputHandelrs';
import Input from '@components/Input';
import TextLink from '@components/TextLink';
import { useCompareItemQuery } from '../utils';
import {
  $GateCompareEditingData,
  $compareLockedApi,
  ComparePlayer,
} from '../compareStores';

export interface PanelCompareProps {
  currentSceneType: SceneType;
  data: SceneCompareItem;
  isNew: boolean;
  onChange: (data: SceneCompareItem) => void;
  onConfirm: (payload: { data: SceneCompareItem; isNew: boolean }) => void;
  onDelete: (id: SceneCompareItem['id']) => void;
  onRequestClose: () => void;
}

const getNav = (
  sceneType: SceneType
): Parameters<typeof selectLibScenes>[0]['nav'] | undefined => {
  if (sceneType === SceneType.pano) return [LibType.pano];
  if (sceneType === SceneType.matrix) return [LibType.matrix];
  if (sceneType === SceneType.panoVideo) return [LibType.panoVideo];
};

export function PanelCompare({
  data,
  currentSceneType,
  isNew,
  onChange,
  onConfirm,
  onDelete,
  onRequestClose,
}: PanelCompareProps) {
  const firstState = useConstant(() => data);
  const { data: cacheData = { data: null, type: data.type } } =
    useCompareItemQuery(data);

  useGate(
    $GateCompareEditingData,
    cacheData ? { ...cacheData, viewOffset: data.viewOffset } : null
  );

  useEffect(() => {
    if (isNew) {
      $compareLockedApi.set(false);
    }
    return () => {
      $compareLockedApi.set(true);
    };
  }, []);

  const titleHandler = useInputHandlers((val) => {
    onChange(
      produce(data, (draft) => {
        draft.title = val;
      })
    );
  });

  const handleSclectScene = async () => {
    const nav = getNav(currentSceneType);
    if (!nav) return;
    let materialId = 0;
    let name = '';
    const { panos, matrices } = await selectLibScenes({ nav });
    if (panos) {
      materialId = panos[0].id;
      name = panos[0].name;
    } else if (matrices) {
      materialId = matrices[0].id;
      name = matrices[0].name;
    }
    onChange(
      produce(data, (draft) => {
        draft.materialId = materialId;
        draft.title = name;
      })
    );
  };

  const handleConfirm = () => {
    const viewOffset = ComparePlayer.getViewOffset();
    onConfirm({ data: { ...data, viewOffset }, isNew });
  };

  const handleDelete = () => {
    if (!isNew) {
      onDelete(data.id);
    }
    onRequestClose();
  };

  const handleClose = () => {
    onRequestClose();
  };

  const handleAfterClose = () => {
    // 未保存直接关恢复视角
    ComparePlayer.resetOffset(data.viewOffset);
  };

  const thumbUrl = (() => {
    if (!cacheData?.data) return '';
    return cacheData.data.thumb;
  })();

  return (
    <PanelBase
      isChanged={() => firstState !== data}
      title={<Title>{isNew ? '添加对比场景' : '编辑对比场景'}</Title>}
      onRequestClose={handleClose}
      onConfirm={handleConfirm}
      onDeleteClick={handleDelete}
      onAfterClose={handleAfterClose}
    >
      <Collapse title="对比场景">
        <Validator
          value={thumbUrl}
          validFn={() => !!thumbUrl}
          warningMessage="请先选择一个场景"
        >
          {({ value, warning }) => (
            <>
              <ImageContainer
                className="h-115"
                warning={warning}
                placeholder="请先选择一个场景"
              >
                {!!value && (
                  <Image
                    src={value}
                    type={ImageType.thumb}
                    className="max-h-full max-w-full"
                  />
                )}
              </ImageContainer>
              <div className="mt-10 flex items-center justify-between">
                <Tip>支持同类型场景</Tip>
                <Button size="sm" onClick={handleSclectScene}>
                  选择场景
                </Button>
              </div>
            </>
          )}
        </Validator>
      </Collapse>
      <Collapse title="标题设置">
        <div className="flex items-center">
          <div>场景标题</div>
          <TextLink
            className="ml-auto"
            disabled={!data.materialId}
            onClick={() => {
              const date = cacheData?.data?.createDate;
              if (date) {
                onChange(
                  produce(data, (draft) => {
                    draft.title = format(fromUnixTime(date), 'yyyy-MM-dd');
                  })
                );
              }
            }}
          >
            填入场景创建日期
          </TextLink>
        </div>
        <Validator
          value={data.title}
          validFn={() => !!data.title}
          warningMessage="请输入场景名称"
        >
          {({ value, warning }) => (
            <Input
              scheme="dark"
              className="mt-10 h-40"
              placeholder="请输入对比场景名称"
              maxLength={50}
              warning={warning}
              value={value}
              {...titleHandler}
            />
          )}
        </Validator>
        <Tip className="mt-10">
          提示：对于工程记录/巡检等应用场景，可将对比场景名称设置为施工/巡检日期以便场景对比时可以更直观查看不同时间点的工程进度/巡检情况
        </Tip>
      </Collapse>
    </PanelBase>
  );
}
